import { Component, OnInit, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AppUpdateService } from 'src/app/services/app-update.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-update-notification',
  template: `
    <div class="update-notification" *ngIf="showUpdateNotification" [@slideIn]>
      <div class="update-content">
        <div class="update-icon" [class.critical]="isCritical">
          <i class="bi" [ngClass]="isCritical ? 'bi-exclamation-triangle-fill' : 'bi-arrow-repeat'"></i>
        </div>
        <div class="update-text">
          <strong>{{ notificationTitle | translate }}</strong>
          <p>{{ notificationMessage | translate }}</p>
        </div>
        <div class="update-actions">
          <button *ngIf="!isCritical" class="btn btn-light btn-sm" (click)="dismissUpdate()">
            {{ 'PWA.DismissButton' | translate }}
          </button>
          <button class="btn btn-primary btn-sm" (click)="updateApp()">
            {{ isCritical ? ('General.Update' | translate) : ('PWA.InstallButton' | translate) }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .update-notification {
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 99999;
      max-width: 420px;
      background: white;
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      border-left: 4px solid var(--konti-accent, #c7aa80);
    }

    .update-content {
      padding: 20px;
      display: flex;
      align-items: flex-start;
      gap: 16px;
    }

    .update-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--konti-mork, #0c0c0c) 0%, #1a1a1a 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: all 0.3s ease;
    }

    .update-icon.critical {
      background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
      border: 2px solid rgba(220, 53, 69, 0.2);
    }

    .update-icon i {
      font-size: 24px;
      color: var(--konti-accent, #c7aa80);
    }

    .update-icon.critical i {
      color: white;
    }

    .update-text {
      flex: 1;
      min-width: 0;
    }

    .update-text strong {
      display: block;
      margin-bottom: 6px;
      color: var(--konti-mork, #0c0c0c);
      font-size: 16px;
      font-weight: 600;
    }

    .update-text p {
      margin: 0;
      font-size: 14px;
      color: #666;
      line-height: 1.5;
    }

    .update-actions {
      display: flex;
      gap: 8px;
      flex-direction: column;
      flex-shrink: 0;
    }

    .update-actions button {
      white-space: nowrap;
      font-weight: 500;
      padding: 8px 16px;
      border-radius: 6px;
      transition: all 0.2s ease;
      min-width: 100px;
    }

    .update-actions .btn-primary {
      background-color: var(--konti-accent, #c7aa80);
      border-color: var(--konti-accent, #c7aa80);
      color: var(--konti-mork, #0c0c0c);
    }

    .update-actions .btn-primary:hover {
      background-color: var(--konti-hover-bg, #d4b890);
      border-color: var(--konti-hover-bg, #d4b890);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(199, 170, 128, 0.3);
    }

    .update-actions .btn-primary:focus {
      box-shadow: 0 0 0 0.2rem rgba(199, 170, 128, 0.5);
    }

    .update-actions .btn-light {
      color: var(--konti-accent, #c7aa80);
      border-color: var(--konti-accent, #c7aa80);
      background-color: transparent;
    }

    .update-actions .btn-light:hover {
      background-color: var(--konti-accent, #c7aa80);
      border-color: var(--konti-accent, #c7aa80);
      color: var(--konti-mork, #0c0c0c);
    }

    .update-actions .btn-light:focus {
      box-shadow: 0 0 0 0.2rem rgba(199, 170, 128, 0.5);
    }

    @media (max-width: 576px) {
      .update-notification {
        left: 12px;
        right: 12px;
        bottom: 12px;
        max-width: none;
      }

      .update-content {
        padding: 16px;
        gap: 12px;
        flex-direction: column;
      }

      .update-icon {
        width: 40px;
        height: 40px;
        align-self: center;
      }

      .update-icon i {
        font-size: 20px;
      }

      .update-text {
        text-align: center;
      }

      .update-text strong {
        font-size: 15px;
      }

      .update-text p {
        font-size: 13px;
      }

      .update-actions {
        width: 100%;
        flex-direction: column-reverse;
      }

      .update-actions button {
        width: 100%;
      }
    }
  `],
  animations: []
})
export class UpdateNotificationComponent implements OnInit, OnDestroy {
  showUpdateNotification = false;
  isCritical = false;
  notificationTitle = 'PWA.UpdateAvailable';
  notificationMessage = 'PWA.UpdateDescription';
  
  private subscriptions: Subscription[] = [];

  constructor(
    private swUpdate: SwUpdate,
    private translate: TranslateService,
    private appUpdateService: AppUpdateService
  ) {}

  ngOnInit(): void {
    // Listen for normal version updates
    if (this.swUpdate.isEnabled) {
      const updateSub = this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is any => evt.type === 'VERSION_READY'))
        .subscribe(() => {
          this.isCritical = false;
          this.notificationTitle = 'PWA.UpdateAvailable';
          this.notificationMessage = 'PWA.UpdateDescription';
          this.showUpdateNotification = true;
        });
      this.subscriptions.push(updateSub);
    }

    // Listen for critical/unrecoverable state
    const criticalSub = this.appUpdateService.unrecoverableState$.subscribe(message => {
      this.isCritical = true;
      this.notificationTitle = 'PWA.UpdateRequired';
      this.notificationMessage = message || 'PWA.UpdateRequired';
      this.showUpdateNotification = true;
    });
    this.subscriptions.push(criticalSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  async updateApp(): Promise<void> {
    this.showUpdateNotification = false;
    
    if (this.swUpdate.isEnabled && !this.isCritical) {
      await this.swUpdate.activateUpdate();
    }
    
    // Reload the page for both critical and normal updates
    document.location.reload();
  }

  dismissUpdate(): void {
    this.showUpdateNotification = false;
    
    // For non-critical updates, show again after 1 hour
    if (!this.isCritical) {
      setTimeout(() => {
        this.showUpdateNotification = true;
      }, 60 * 60 * 1000);
    }
  }
}
